import Head from 'next/head'
import { useRouter } from 'next/router'

interface SEOProps {
  title: string
  description?: string
  keywords?: string[]
  image?: string
  shouldExcludeTitleSuffix?: boolean
  shouldIndexPage?: boolean
}

export default function SEO({
  title,
  description,
  keywords,
  image,
  shouldExcludeTitleSuffix = false,
  shouldIndexPage = true
}: SEOProps) {
  const router = useRouter()
  const siteUrl = (
    process.env.NEXT_PUBLIC_SITE_URL || 'https://carlos-miguel.dev'
  ).replace(/\/$/, '')
  const canonicalPath = router.asPath.split(/[?#]/)[0] || '/'
  const canonicalUrl = `${siteUrl}${canonicalPath}`
  const pageTitle = shouldExcludeTitleSuffix ? title : `${title} - Solrachix`
  const pageImage = image
    ? image.startsWith('/')
      ? `${siteUrl}${image}`
      : image
    : null

  return (
    <Head>
      <title>{pageTitle}</title>
      {description && <meta name="description" content={description} />}
      {pageImage && <meta name="image" content={pageImage} />}
      {keywords && (
        <meta name="keywords" content={keywords.slice().join(', ')} />
      )}

      <meta
        name="robots"
        content={shouldIndexPage ? 'index,follow' : 'noindex,nofollow'}
      />
      {shouldIndexPage && <link rel="canonical" href={canonicalUrl} />}

      <link rel="apple-touch-icon" href="/apple-icon.png" />
      <link rel="shortcut icon" href="/favicon.png" type="image/png" />

      <link rel="icon" href="/favicon.png" />
      <link rel="manifest" href="/manifest.json" />

      <meta httpEquiv="x-ua-compatible" content="IE=edge,chrome=1" />
      <meta name="MobileOptimized" content="320" />
      <meta name="HandheldFriendly" content="True" />
      <meta name="theme-color" content="#121214" />
      <meta name="msapplication-TileColor" content="#121214" />
      <meta name="referrer" content="no-referrer-when-downgrade" />
      <meta name="google" content="notranslate" />

      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:locale" content="pt_BR" />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={pageTitle} />
      <meta property="og:image" content={pageImage} />
      <meta property="og:image:secure_url" content={pageImage} />
      <meta property="og:image:alt" content="Thumbnail" />
      <meta property="og:image:type" content="image/png" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@solrachix" />
      <meta name="twitter:creator" content="@solrachix" />
      <meta name="twitter:image" content={pageImage} />
      <meta name="twitter:image:src" content={pageImage} />
      <meta name="twitter:image:alt" content="Thumbnail" />
      <meta name="twitter:image:width" content="1200" />
      <meta name="twitter:image:height" content="620" />

      <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
      <meta
        name="viewport"
        content="width=device-width,initial-scale=1,minimum-scale=1,maximum-scale=5" // ,user-scalable=no
      />
    </Head>
  )
}
